// CyberWISE shell — @wise/ui chrome, rail and mobile drawer; server-driven
// navigation (GET /me → views); the handoff's views as the page content.
//
// ROLE GATING HERE ONLY HIDES AFFORDANCES. Every permission is re-enforced
// server-side. The rail renders the intersection of NAV (labels, order,
// icons) with `me.views` (policy). There is no client-side role switcher, and
// View as is refused for this module (see api/src/iam.ts).
//
// Light only: the handoff stylesheet is full of literal colours, so dark mode
// is not declared and the chrome shows no theme switch.

import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  UserMenu,
  WiseChrome,
  WiseShell,
  WiseSignIn,
  WiseThemeProvider,
  useM365Photo,
  useWiseMobile,
  type WiseBrand,
  type WiseLinkProps,
  type WiseNavItem,
} from '@wise/ui';
import { loginRequest } from './auth/msal';
import { addressWithoutLoginHint, linkLoginHint } from './auth/linkHint';
import {
  arrivalLoginHint,
  claimAutoSignIn,
  markSignedOut,
  shouldSignInAutomatically,
  signedInAccount,
  signedOutOnPurpose,
} from './auth/ssoMemory';
import { AlertsBell } from './components/AlertsBell';
import { HealthPill, NAV_ICONS, PageHeader } from './components/Shell';
import { StateMessage } from './components/StateMessage';
import { Direction, DirectionSwitch, Posture, readDirection, writeDirection } from './components/views/Posture';
import { Awareness, Controls, Deadlines, Incidents, Requests, Vendors } from './components/views/ProgramViews';
import { AdminConnectors } from './components/views/AdminConnectors';
import { Drawer } from './components/drawer/Drawer';
import { buildDrawer, type DrawerTarget } from './components/drawer/content';
import { DrawerProvider } from './components/drawer/context';
import { api, ApiError, graphToken } from './lib/api';
import type { Me, PostureResponse, ProgramResponse, ViewKey } from './lib/types';

const MODULE_NAME = 'CyberWISE';
const MODULE_ID = 'itms-cyber';
// The PROD Dial store. CyberWISE has one environment, so the fallback is
// prod's — the pipeline pins the same value, and its bundle assertion fails
// any build that mentions the dev store (run 1918 caught the old dev default).
const WISE_DIAL_URL =
  (import.meta.env.VITE_WISE_DIAL_URL as string | undefined) ?? 'https://stwiseiamprod.blob.core.windows.net/dial/tokens.json';
// The WISE chip goes home to the Launchpad in PRODUCTION only, recognised by
// the custom domain (given at prod cutover, never before). Elsewhere it is a
// plain label. It never points at wise-shell — that is the TEST home.
const WISE_HOME_URL = /\.(westportproperties|usstorage)\.net$/i.test(window.location.hostname)
  ? 'https://wise.westportproperties.net'
  : undefined;

const BRAND_LOCKUP: Record<WiseBrand, string> = { WPI: '/brands/wpi/logo-light.png', USSC: '/brands/ussc/logo-light.svg' };

interface NavEntry {
  view: ViewKey;
  label: string;
  section: 'signal' | 'compliance' | 'intake' | 'people' | 'admin';
  path: string;
  title: string;
}

/** The handoff's rail, in its order and with its words, plus Administration. */
const NAV: readonly NavEntry[] = [
  { view: 'posture', label: 'Posture', section: 'signal', path: '/posture', title: 'Posture' },
  { view: 'incidents', label: 'Incidents', section: 'signal', path: '/incidents', title: 'Incidents' },
  { view: 'controls', label: 'Controls', section: 'compliance', path: '/controls', title: 'Controls' },
  { view: 'deadlines', label: 'Deadlines', section: 'compliance', path: '/deadlines', title: 'Deadlines' },
  { view: 'requests', label: 'Requests', section: 'intake', path: '/requests', title: 'Requests' },
  { view: 'vendors', label: 'Vendor Risk', section: 'intake', path: '/vendors', title: 'Vendor Risk' },
  { view: 'awareness', label: 'Awareness', section: 'people', path: '/awareness', title: 'Awareness' },
  { view: 'connectors', label: 'Connectors', section: 'admin', path: '/admin/connectors', title: 'Connectors' },
];
const SECTION_LABELS: Record<NavEntry['section'], string> = {
  signal: 'Signal',
  compliance: 'Compliance',
  intake: 'Intake',
  people: 'People',
  admin: 'Administration',
};

function RouteLink({ to, className, style, children, onClick, onMouseEnter, onMouseLeave }: WiseLinkProps) {
  return (
    <Link to={to} className={className} style={style} onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {children}
    </Link>
  );
}

function brandOf(email: string): WiseBrand {
  return email.toLowerCase().endsWith('@usstorage.net') ? 'USSC' : 'WPI';
}

function BrandLockup({ brand, compact }: { brand: WiseBrand; compact: boolean }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span style={{ color: '#fff', fontSize: 15, fontWeight: 800 }}>Westport Properties</span>;
  return (
    <img
      src={BRAND_LOCKUP[brand]}
      alt={brand === 'USSC' ? 'US Storage Centers' : 'Westport Properties'}
      onError={() => setFailed(true)}
      // 26px is the platform lockup; on a phone it pushes the WISE chip under
      // the bell, so it steps down there.
      style={{ height: compact ? 16 : 26, width: 'auto', display: 'block' }}
    />
  );
}

function Loading() {
  return (
    <div className="content">
      <div className="stats c5">
        {Array.from({ length: 5 }, (_, i) => (
          <div className="stat" key={i}>
            <div className="cw-skel" style={{ width: '55%' }} />
            <div className="cw-skel" style={{ width: '35%', height: 22, marginTop: 10 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

type MeState = { status: 'loading' } | { status: 'ready'; me: Me } | { status: 'failed'; error: ApiError };

/** The program views. Mounted only once /me says the person holds them. */
function ProgramArea({
  me,
  direction,
  onConnectors,
}: {
  me: Me;
  direction: Direction;
  onConnectors: (c: PostureResponse['connectors']) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [posture, setPosture] = useState<PostureResponse | null>(null);
  const [program, setProgram] = useState<ProgramResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState<DrawerTarget | null>(null);
  const canView = me.views.includes('posture');

  const load = useCallback(() => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    Promise.all([api.posture(), api.program()])
      .then(([p, pr]) => {
        setPosture(p);
        setProgram(pr);
        onConnectors(p.connectors);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err : new ApiError(0, String(err))))
      .finally(() => setLoading(false));
  }, [canView, onConnectors]);

  useEffect(load, [load]);

  // Content scroll resets to the top on view change, per the handoff. The
  // scroller is the shell's <main> now, not the handoff's .content.
  useEffect(() => {
    document.querySelector('main')?.scrollTo({ top: 0 });
  }, [location.pathname]);

  const connectors = posture?.connectors ?? [];
  const content =
    drawer && program
      ? buildDrawer(drawer, program, connectors, setDrawer, (path) => {
          setDrawer(null);
          navigate(path);
        })
      : null;

  if (canView && error) {
    return (
      <div className="content">
        <StateMessage
          title="CyberWISE could not load"
          body={
            error.code === 'IAM_UNAVAILABLE'
              ? 'Permissions could not be verified right now, so CyberWISE is not serving data. This is deliberate — try again shortly.'
              : 'The program database or the connector layer could not be reached.'
          }
          detail={error.detail ?? error.message}
          onRetry={load}
        />
      </div>
    );
  }

  const home = canView ? '/posture' : '/admin/connectors';
  return (
    <DrawerProvider value={setDrawer}>
      <Routes>
        <Route path="/" element={<Navigate to={home} replace />} />
        {canView && (
          <Route
            path="/posture"
            element={
              <>
                <PageHeader
                  title="Posture"
                  subtitle="Signal from every connected source, with the compliance program beside it"
                />
                <div className="content">{loading || !posture ? <Loading /> : <Posture data={posture} direction={direction} />}</div>
              </>
            }
          />
        )}
        {canView && <Route path="/incidents" element={<Incidents program={program} loading={loading} />} />}
        {canView && <Route path="/controls" element={<Controls program={program} loading={loading} />} />}
        {canView && <Route path="/deadlines" element={<Deadlines program={program} loading={loading} />} />}
        {canView && <Route path="/requests" element={<Requests program={program} loading={loading} />} />}
        {canView && <Route path="/vendors" element={<Vendors program={program} loading={loading} />} />}
        {canView && <Route path="/awareness" element={<Awareness program={program} loading={loading} />} />}
        {me.views.includes('connectors') && <Route path="/admin/connectors" element={<AdminConnectors />} />}
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>

      <Drawer
        open={!!content}
        eyebrow={content?.eyebrow ?? ''}
        title={content?.title ?? ''}
        meta={content?.meta}
        footer={content?.footer}
        onClose={() => setDrawer(null)}
      >
        {content?.body}
      </Drawer>
    </DrawerProvider>
  );
}

function NoAccess({ me }: { me: Me }) {
  return (
    <div className="content">
      <StateMessage
        title="CyberWISE is restricted to the Cyber Security Department"
        body={`You are signed in as ${me.user.email}, which holds no CyberWISE role. Access is granted in WISE-IAM (module itms-cyber) — Security Analyst to read the program, Security Administrator to manage connector credentials.`}
      />
    </div>
  );
}

function AppShell() {
  const { instance, accounts } = useMsal();
  // The ACTIVE account — the link's person, or whoever just signed in — never
  // simply the first cached account (auth/ssoMemory.ts).
  const account = signedInAccount(instance, accounts);
  const email = account?.username ?? '';
  const location = useLocation();
  const navigate = useNavigate();

  // Signed in: the link's ?login_hint has done its work. An address copied
  // from here into Teams must not carry THIS person's hint — the colleague
  // who opens it would be signed in as, or asked the password of, somebody
  // else (auth/linkHint.ts). Through the router, so a view that rewrites its
  // own query (ProgramViews' filters) never writes the hint back; the path,
  // the hash and every other key stay.
  useEffect(() => {
    const url = addressWithoutLoginHint(window.location.pathname, window.location.search, window.location.hash);
    if (url !== null) navigate(url, { replace: true, state: location.state });
    // Once, on arrival signed in.
  }, []);
  const isMobile = useWiseMobile();
  const photoUrl = useM365Photo(graphToken);
  const [meState, setMeState] = useState<MeState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const [connectors, setConnectors] = useState<PostureResponse['connectors']>([]);
  // Both landing directions ship behind the header switch (decision
  // 2026-08-12), persisted under `cyberwise-dir`. Only Posture differs.
  const [direction, setDirection] = useState<Direction>(readDirection);
  const chooseDirection = (d: Direction) => {
    setDirection(d);
    writeDirection(d);
  };

  useEffect(() => {
    let cancelled = false;
    setMeState({ status: 'loading' });
    api
      .me()
      .then((me) => !cancelled && setMeState({ status: 'ready', me }))
      .catch((err: unknown) => !cancelled && setMeState({ status: 'failed', error: err instanceof ApiError ? err : new ApiError(0, String(err)) }));
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const me = meState.status === 'ready' ? meState.me : null;
  const hasAccess = me !== null && me.views.length > 0;

  const current = NAV.find((n) => location.pathname === n.path || location.pathname.startsWith(`${n.path}/`));
  const title = current?.title ?? '';
  useEffect(() => {
    document.title = title ? `${title} · ${MODULE_NAME}` : MODULE_NAME;
  }, [title]);

  const navItems: WiseNavItem[] = useMemo(() => {
    if (!me) return [];
    const granted = new Set<string>(me.views);
    return NAV.filter((n) => granted.has(n.view)).map((n) => ({
      to: n.path,
      label: n.label,
      section: n.section,
      icon: NAV_ICONS[n.view],
    }));
  }, [me]);

  const canManage = me?.views.includes('connectors') ?? false;
  const roleLabel = me ? me.roles.map((r) => r.replace(/_/g, ' ')).join(' · ') || 'no CyberWISE role' : undefined;

  const chrome = (
    <WiseChrome
      lockup={<BrandLockup brand={brandOf(email)} compact={isMobile} />}
      system={MODULE_NAME}
      page={title}
      compact={isMobile}
      onLockupClick={() => hasAccess && navigate('/')}
      wiseShellUrl={WISE_HOME_URL}
      loginHint={email || undefined}
      themeToggle={false}
      right={
        <>
          {/* Hidden on every view except Posture — it does nothing elsewhere,
              and a control that does nothing is worse than no control. */}
          {hasAccess && location.pathname === '/posture' && !isMobile && (
            <DirectionSwitch value={direction} onChange={chooseDirection} />
          )}
          {/* The handoff puts connector health in the chrome: a degraded
              connector must be visible without a click. */}
          {hasAccess && !isMobile && (
            <HealthPill connectors={connectors} onOpen={canManage ? () => navigate('/admin/connectors') : undefined} />
          )}
          {/* Platform convention: the bell sits immediately left of the avatar. */}
          {hasAccess && <AlertsBell compact={isMobile} />}
          <UserMenu
            user={{ name: me?.user.displayName || account?.name || email, email, photoUrl, ...(roleLabel ? { role: roleLabel } : {}) }}
            items={[
              {
                label: 'Sign out',
                danger: true,
                // A CHOICE: remembered, so no link signs them straight back in
                // (auth/ssoMemory.ts) — until they sign in again.
                onClick: () => {
                  markSignedOut();
                  void instance.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin });
                },
              },
            ]}
          />
        </>
      }
    />
  );

  let body: ReactNode;
  if (meState.status === 'loading') {
    body = <Loading />;
  } else if (meState.status === 'failed') {
    const e = meState.error;
    body = (
      <div className="content">
        <StateMessage
          title={e.code === 'IAM_UNAVAILABLE' ? 'Permissions could not be verified' : 'CyberWISE could not be reached'}
          body={
            e.code === 'IAM_UNAVAILABLE'
              ? 'CyberWISE will not fall back to showing what a generic corporate sign-in can reach. Nothing is broken and nothing has been lost — this usually clears within a few minutes.'
              : 'This is a connection problem, not a permissions one.'
          }
          detail={e.message}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </div>
    );
  } else if (!hasAccess) {
    body = <NoAccess me={meState.me} />;
  } else {
    body = <ProgramArea me={meState.me} direction={direction} onConnectors={setConnectors} />;
  }

  return (
    <WiseShell
      chrome={chrome}
      contentPadding={0}
      contentPaddingMobile={0}
      nav={
        hasAccess
          ? {
              items: navItems,
              isActive: (to) => location.pathname === to || location.pathname.startsWith(`${to}/`),
              linkComponent: RouteLink,
              sectionLabels: SECTION_LABELS,
              sidebarProps: { persistKey: 'cyberwise:nav' },
              drawerHeader: (
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800 }}>{MODULE_NAME}</div>
                  <div style={{ fontSize: 11.5, opacity: 0.7 }}>Security Program Operations</div>
                </div>
              ),
            }
          : undefined
      }
    >
      <div className="cw-page">{body}</div>
    </WiseShell>
  );
}

/** Neither the page carrying Entra's answer nor MSAL's hidden renewal frame
 *  ever starts a sign-in of its own. */
const onAuthResponseOrInFrame = (): boolean =>
  /[?#&](code|error)=/.test(window.location.hash + window.location.search) || window.self !== window.top;

/** Who the sign-in names: the link's own hint, else whoever last signed in here. */
const arrivalRequest = () => {
  const hint = arrivalLoginHint(linkLoginHint(window.location.search, window.location.hash));
  return { ...loginRequest, ...(hint ? { loginHint: hint } : {}) };
};

/** The Sign in a person presses: as the arrival — but after a CHOSEN Sign out
 *  only the link's own hint, never the last person, who chose to leave (on a
 *  shared PC that is somebody else, asked for the leaver's password). */
const buttonRequest = () => {
  const link = linkLoginHint(window.location.search, window.location.hash);
  const hint = signedOutOnPurpose() ? link : arrivalLoginHint(link);
  return { ...loginRequest, ...(hint ? { loginHint: hint } : {}) };
};

function SignIn() {
  const { instance, inProgress } = useMsal();
  // NEVER A SIGN-IN PAGE FOR SOMEONE WITH AN ENTRA SESSION (the platform rule,
  // Chauncey White 2026-10-23: "EVERY link emailed or sent or coded should
  // check to see if the user is logged in"). Go straight to Entra — with the
  // link's login_hint, else whoever last signed in on this browser — ONCE per
  // tab (a round trip that comes back without a session lands here and gets
  // the page), never after a chosen Sign out, and only while MSAL is idle
  // (`interaction_in_progress` would burn the one try). Seeded during render
  // so the sign-in page never flashes before the redirect leaves.
  const [redirecting, setRedirecting] = useState(() => shouldSignInAutomatically() && !onAuthResponseOrInFrame());
  const started = useRef(false);
  useEffect(() => {
    if (started.current || inProgress !== InteractionStatus.None) return;
    if (onAuthResponseOrInFrame() || !shouldSignInAutomatically() || !claimAutoSignIn()) {
      setRedirecting(false);
      return;
    }
    started.current = true;
    instance.loginRedirect(arrivalRequest()).catch((err: unknown) => {
      // The redirect could not start: the sign-in page, never a stuck splash.
      console.warn('[cyberwise] automatic sign-in redirect failed:', err);
      setRedirecting(false);
    });
  }, [instance, inProgress]);

  if (redirecting) {
    // A sign-in in progress, not a request to sign in.
    return (
      <div role="status" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: '#5b6b7f', fontSize: 14 }}>
        Signing you in to {MODULE_NAME}…
      </div>
    );
  }
  return (
    <WiseSignIn
      title={MODULE_NAME}
      tagline="Security program operations: posture across the security tools, the compliance program, deadlines, intake, vendor risk and incidents."
      subline="Restricted to the Cyber Security Department."
      logos={['/brands/wpi/logo-dark.png']}
      logoAlts={['Westport Properties']}
      onSignIn={() => void instance.loginRedirect(buttonRequest())}
    />
  );
}

export function App() {
  const { instance, accounts } = useMsal();
  return (
    <WiseThemeProvider userEmail={signedInAccount(instance, accounts)?.username ?? null} tokensUrl={WISE_DIAL_URL} moduleId={MODULE_ID}>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AppShell />
      </AuthenticatedTemplate>
    </WiseThemeProvider>
  );
}
